import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    IconButton,
    Box,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useRuleService } from '../../../../../../context/RuleServiceContext';
import { getOperators, RuleTenant } from '../../../../../../helper/RuleServiceV2.const';
import { RuleServiceQuery } from '../../../../../../../../services/query';
import { RTG } from './ruleTabGrid.classes';

const tableCellStyle: any = {
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    height: '64px',
};

export const RuleTabGrid: FC<any> = ({ setIsRuleModalOpen, setSelectedRule }) => {
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [selectedPopoverRule, setSelectedPopoverRule] = useState<any>(false);

    const { t } = useTranslation();

    const {
        gridData,
        tenant,
        totalCount,
        setPage,
        page,
        rowsPerPage,
        setRowsPerPage,
        fetchRuleGridData,
        setIsLoading,
    } = useRuleService();

    const operators = getOperators();

    const handleChangePage = (_: any, newPage: any) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleOpenDialog = (rule: any) => {
        setSelectedPopoverRule(rule);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedPopoverRule(null);
    };

    const getDate = (date: any) => {
        return new Date(date).toLocaleDateString('tr-TR');
    };

    const deleteRule = async (id: number) => {
        setIsLoading(true);
        RuleServiceQuery.DeleteRule(id)
            .then(() => {
                fetchRuleGridData();
                Swal.fire(t('general.success'), ' ', 'success');
            })
            .catch(() => {
                Swal.fire(t('ruleManagement.notify.rule.deleteNewRuleFailed'), '', 'error');
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchRuleGridData();
    }, [tenant, rowsPerPage, page, fetchRuleGridData]);

    return (
        <>
            <Paper className={RTG.tablePaper}>
                <Table className={RTG.table}>
                    <TableHead className={RTG.head}>
                        <TableRow className={RTG.headRow}>
                            <TableCell className={RTG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.ruleTemplate.id.name')}
                            </TableCell>
                            <TableCell className={RTG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.ruleTemplate.domain.name')}
                            </TableCell>
                            <TableCell className={RTG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.rule.ruleKey.name')}
                            </TableCell>
                            <TableCell className={RTG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.rule.effectiveDate.nameV2')}
                            </TableCell>
                            <TableCell className={RTG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.rule.criteria.nameV2')}
                            </TableCell>
                            <TableCell className={RTG.headCell} style={{ textAlign: 'center' }}>
                                {t('general.actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className={RTG.body}>
                        {(gridData || []).map((rule: any, key: number) => {
                            return (
                                <TableRow className={RTG.row} key={key} data-rule-id={rule.id}>
                                    <TableCell className={[RTG.cell, RTG.cellId].join(' ')} style={{ ...tableCellStyle }}>
                                        {rule.id}
                                    </TableCell>
                                    <TableCell className={[RTG.cell, RTG.cellTenant].join(' ')} style={{ ...tableCellStyle }}>
                                {RuleTenant[rule.tenant as keyof typeof RuleTenant]}
                                    </TableCell>
                                    <TableCell className={[RTG.cell, RTG.cellRuleKey].join(' ')} style={{ ...tableCellStyle }}>
                                        {rule.ruleKey}
                                    </TableCell>
                                    <TableCell className={[RTG.cell, RTG.cellEffectiveDate].join(' ')} style={{ ...tableCellStyle }}>
                                        {getDate(rule.effectiveDate)}
                                    </TableCell>
                                    <TableCell className={[RTG.cell, RTG.cellCriteriaCount].join(' ')} style={{ ...tableCellStyle }}>
                                {rule.criteria?.length > 0 ? (
                                    <Chip
                                        className={RTG.criteriaChip}
                                        sx={{ marginLeft: '4px' }}
                                        label={`${rule.criteria?.length}`}
                                        onClick={() => handleOpenDialog(rule)}
                                        color="warning"
                                    />
                                ) : (
                                    <Typography>-</Typography>
                                )}
                                    </TableCell>
                                    <TableCell className={[RTG.cell, RTG.cellActions].join(' ')} style={{ textAlign: 'center' }}>
                                <IconButton
                                    className={RTG.editButton}
                                    color="primary"
                                    onClick={() => {
                                        setSelectedRule(rule);
                                        setIsRuleModalOpen(true);
                                    }}
                                >
                                    <EditIcon className={RTG.editIcon} sx={{ width: '16px', height: '16px' }} />
                                </IconButton>
                                <IconButton
                                    className={RTG.deleteButton}
                                    color="error"
                                    onClick={() => {
                                        Swal.fire({
                                            title: 'Silmek istediğinizden emin misiniz?',
                                            icon: 'warning',
                                            showCancelButton: true,
                                            confirmButtonText: t('general.yes'),
                                            cancelButtonText: t('general.no'),
                                        }).then((result) => {
                                            if (result.isConfirmed) {
                                                deleteRule(rule.id);
                                            }
                                        });
                                    }}
                                >
                                    <DeleteOutlineIcon className={RTG.deleteIcon} sx={{ width: '16px', height: '16px' }} />
                                </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>
            <TablePagination
                className={RTG.pagination}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <Dialog className={RTG.detailsDialog} open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle className={RTG.detailsDialogTitle}>
                    <Box className={RTG.detailsDialogTitleLayout} sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', width: '100%' }}>
                        {t('ruleManagement.fields.rule.detail') + ' - '}
                        <span className={RTG.detailsDialogTitleKey} style={{ color: 'blue' }}>{`(${selectedPopoverRule?.ruleKey || ''})`}</span>
                        <IconButton className={RTG.detailsDialogCloseButton} sx={{ marginLeft: 'auto' }}>
                            <CloseIcon className={['svg_icons', RTG.detailsDialogCloseIcon].filter(Boolean).join(' ')} onClick={handleCloseDialog} />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={RTG.detailsDialogContent}>
                    {selectedPopoverRule && selectedPopoverRule?.criteria?.length > 0 && (
                        <Paper className={RTG.detailsTablePaper} style={{ marginBottom: '2rem' }}>
                            <Table className={RTG.detailsTable}>
                                <TableHead className={RTG.detailsHead}>
                                    <TableRow className={RTG.detailsHeadRow}>
                                        <TableCell className={RTG.detailsHeadCell} style={{ textAlign: 'center' }}>
                                        {t('ruleManagement.fields.criteriaType.key.name')}
                                        </TableCell>
                                        <TableCell className={RTG.detailsHeadCell} style={{ textAlign: 'center' }}>
                                        {t('ruleManagement.operatorV2')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={RTG.detailsBody}>
                                    {(selectedPopoverRule?.criteria || []).map((item: any, index: number) => (
                                        <TableRow className={RTG.detailsRow} key={index} data-criteria-index={index}>
                                            <TableCell className={RTG.detailsCell} style={{ textAlign: 'center' }}>
                                            {item.key}
                                            </TableCell>
                                            <TableCell className={RTG.detailsCell} style={{ textAlign: 'center' }}>
                                            {operators?.[item.operator]?.label}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
